import { useLayoutEffect } from "react";

/**
 * Scroll-position-linked parallax for marketing rows.
 *
 * Any element with `data-parallax="left" | "right" | "up"` inside `rootRef` is driven
 * by its own position in the viewport: as it rises into the comfortable reading zone it
 * settles to its resting place and becomes fully opaque; scrolling back up separates it
 * (left moves further left, right further right, up moves down) and fades it out.
 *
 * The effect is continuous and reversible (tied to scroll, not a one-shot trigger).
 * Respects prefers-reduced-motion.
 *
 * @param {React.RefObject<HTMLElement>} rootRef
 * @param {any} deps - re-run trigger (e.g. language)
 */
export function useScrollParallax(rootRef, deps) {
  useLayoutEffect(() => {
    const root = rootRef?.current;
    if (!root) return;

    const els = Array.from(root.querySelectorAll("[data-parallax]"));
    if (!els.length) return;

    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      els.forEach((el) => {
        el.style.opacity = "";
        el.style.transform = "";
      });
      return;
    }

    const SHIFT = 72; // px the columns spread apart at progress 0
    const LIFT = 44; // px the "up" elements drop at progress 0
    els.forEach((el) => {
      el.style.willChange = "opacity, transform";
      el.style.transition = "none";
    });

    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const start = vh * 0.98; // progress 0: element top near the bottom of the viewport
      const end = vh * 0.45; // progress 1: element top settled in the upper-middle
      const span = Math.max(1, start - end);
      for (const el of els) {
        const top = el.getBoundingClientRect().top;
        let p = (start - top) / span;
        p = p < 0 ? 0 : p > 1 ? 1 : p;
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        const away = 1 - eased;
        const kind = el.getAttribute("data-parallax");
        let tx = 0;
        let ty = 0;
        if (kind === "left") tx = -away * SHIFT;
        else if (kind === "right") tx = away * SHIFT;
        else ty = away * LIFT; // "up"
        el.style.opacity = String(0.05 + eased * 0.95);
        el.style.transform = `translate3d(${tx.toFixed(1)}px, ${ty.toFixed(1)}px, 0)`;
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    update(); // set initial state before paint (no flash)
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      els.forEach((el) => {
        el.style.willChange = "";
      });
    };
  }, [rootRef, deps]);
}
